import { useState, useEffect, useCallback, forwardRef } from "react";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Send } from "lucide-react";

const WEB3FORMS_KEY = "31665aca-c778-423f-b9e2-4946f88ed2d7";

const sectors = [
  "Tecnología / Software",
  "Servicios profesionales",
  "E-commerce / Retail",
  "Manufactura",
  "Educación",
  "Salud",
  "Otro",
];

const projectTypes = [
  "Nuevo desarrollo de software a medida",
  "Mejorar o escalar un sistema existente",
  "Integraciones entre sistemas / APIs",
  "Migración o modernización en la nube",
  "Proyecto de datos / analítica / dashboards",
  "No estoy seguro, necesito orientación",
];

const projectStates = [
  "Solo una idea general",
  "Tenemos requisitos más o menos definidos",
  "Ya tenemos un sistema y queremos mejorarlo",
  "Ya intentamos con otro proveedor y no funcionó",
];

const timelines = [
  "Lo antes posible (1-2 meses)",
  "En los próximos 3-6 meses",
  "Más de 6 meses",
  "Solo quiero información por ahora",
];

const objectives = [
  "Automatizar procesos y ahorrar tiempo",
  "Escalar operación y soportar más usuarios/clientes",
  "Reducir errores / mejorar calidad de la información",
  "Mejorar experiencia de usuario",
];

const diagOptions = [
  { value: "agendar", label: "Sí, quiero agendar una llamada" },
  { value: "correo", label: "Prefiero que me contacten por correo" },
];

const inputStyles = "bg-transparent border-0 border-b border-white/20 rounded-none text-white focus-visible:ring-0 focus-visible:border-white transition-colors py-3 placeholder:text-neutral-600 text-sm";
const labelStyles = "text-xs font-mono tracking-[0.15em] uppercase text-neutral-400 mb-2 block";

const LeadForm = forwardRef((_props, ref) => {
  const viewRef = useRef(null);
  const isInView = useInView(viewRef, { once: true, margin: "-50px" });

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const captchaRef = useRef(null);
  const captchaWidgetId = useRef(null);

  const renderCaptcha = useCallback(() => {
    if (window.hcaptcha && captchaRef.current && captchaWidgetId.current === null) {
      captchaWidgetId.current = window.hcaptcha.render(captchaRef.current, {
        sitekey: "50b2fe65-b00b-4b9e-ad62-3ba471098be2",
        theme: "dark",
      });
    }
  }, []);

  useEffect(() => {
    // hCaptcha may already be loaded or load later
    if (window.hcaptcha) {
      renderCaptcha();
    } else {
      window.addEventListener("hcaptchaLoaded", renderCaptcha);
      // Also poll briefly in case the event doesn't fire
      const interval = setInterval(() => {
        if (window.hcaptcha) {
          renderCaptcha();
          clearInterval(interval);
        }
      }, 500);
      return () => {
        window.removeEventListener("hcaptchaLoaded", renderCaptcha);
        clearInterval(interval);
      };
    }
  }, [renderCaptcha]);
  const [formData, setFormData] = useState({
    nombre_completo: "",
    correo_empresa: "",
    telefono: "",
    nombre_empresa: "",
    sitio_web: "",
    sector: "",
    sector_otro: "",
    tipo_proyecto: [],
    estado_proyecto: "",
    descripcion_reto: "",
    timeline: "",
    objetivo_principal: [],
    objetivo_otro: "",
    interes_diagnostico: "",
  });

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const toggleCheckbox = (field, value) => {
    setFormData((prev) => {
      const arr = prev[field];
      return {
        ...prev,
        [field]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.nombre_completo || !formData.correo_empresa || !formData.nombre_empresa) {
      toast.error("Por favor completa los campos obligatorios");
      return;
    }
    if (!formData.sector) {
      toast.error("Por favor selecciona un sector");
      return;
    }
    if (formData.tipo_proyecto.length === 0) {
      toast.error("Por favor selecciona al menos un tipo de proyecto");
      return;
    }
    if (!formData.estado_proyecto) {
      toast.error("Por favor indica el estado de tu proyecto");
      return;
    }

    setLoading(true);
    try {
      const sector = formData.sector === "Otro" ? formData.sector_otro || "Otro" : formData.sector;
      const objetivos = formData.objetivo_otro
        ? [...formData.objetivo_principal, formData.objetivo_otro]
        : formData.objetivo_principal;

      const data = {
        access_key: WEB3FORMS_KEY,
        subject: `Nuevo lead: ${formData.nombre_empresa}`,
        from_name: "Zulunity Landing",
        nombre_completo: formData.nombre_completo,
        correo_empresa: formData.correo_empresa,
        telefono: formData.telefono,
        nombre_empresa: formData.nombre_empresa,
        sitio_web: formData.sitio_web,
        sector,
        tipo_proyecto: formData.tipo_proyecto.join(", "),
        estado_proyecto: formData.estado_proyecto,
        descripcion_reto: formData.descripcion_reto,
        timeline: formData.timeline,
        objetivo_principal: objetivos.join(", "),
        interes_diagnostico: formData.interes_diagnostico,
      };

      // Obtener token hCaptcha
      if (window.hcaptcha && captchaWidgetId.current !== null) {
        const token = window.hcaptcha.getResponse(captchaWidgetId.current);
        if (!token) {
          toast.error("Por favor completa el captcha");
          setLoading(false);
          return;
        }
        data["h-captcha-response"] = token;
      }

      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(data),
      });

      const result = await res.json();
      if (result.success) {
        setSubmitted(true);
        toast.success("¡Solicitud enviada correctamente!");
      } else {
        toast.error(result.message || "Error al enviar. Inténtalo de nuevo.");
      }
    } catch {
      toast.error("Error al enviar. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
      if (window.hcaptcha && captchaWidgetId.current !== null) {
        window.hcaptcha.reset(captchaWidgetId.current);
      }
    }
  };

  if (submitted) {
    return (
      <section
        id="formulario"
        data-testid="form-section"
        ref={ref}
        className="py-24 sm:py-32"
        style={{ backgroundColor: "#050505" }}
      >
        <div className="max-w-2xl mx-auto px-4 sm:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <CheckCircle2 className="w-16 h-16 text-white mx-auto mb-6" />
            <h2
              className="text-3xl sm:text-4xl font-bold text-white mb-4"
              style={{ fontFamily: "'Syne', sans-serif" }}
            >
              ¡Solicitud enviada!
            </h2>
            <p className="text-neutral-400 text-base leading-relaxed">
              Gracias por tu interés en Zulunity. Revisaremos tu información y te
              contactaremos por correo en un plazo máximo de 2 días hábiles para
              contarte los siguientes pasos.
            </p>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="formulario"
      data-testid="form-section"
      ref={(el) => {
        viewRef.current = el;
        if (typeof ref === "function") ref(el);
        else if (ref) ref.current = el;
      }}
      className="py-24 sm:py-32"
      style={{ backgroundColor: "#050505" }}
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <span className="text-xs font-mono tracking-[0.25em] uppercase text-neutral-500 block mb-4">
            Formulario
          </span>
          <h2
            data-testid="form-title"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white max-w-2xl"
            style={{ fontFamily: "'Syne', sans-serif" }}
          >
            Aplica al desarrollo gratis
          </h2>
          <p className="text-base text-neutral-400 leading-relaxed mt-4">
            Completa el formulario y revisaremos si tu proyecto aplica para esta campaña.
          </p>
        </motion.div>

        <form onSubmit={handleSubmit} className="space-y-12" data-testid="lead-form">
          {/* Block 1 - Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="form-block border border-white/[0.06] p-8 sm:p-10 bg-[#0A0A0A]"
          >
            <h3
              className="text-lg font-semibold text-white mb-8 flex items-center gap-3"
              style={{ fontFamily: "'Syne', sans-serif" }}
            >
              <span className="text-xs font-mono text-neutral-500 border border-white/10 w-7 h-7 flex items-center justify-center">1</span>
              Datos de contacto
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              <div>
                <Label className={labelStyles} htmlFor="nombre_completo">Nombre completo *</Label>
                <Input
                  data-testid="input-nombre"
                  id="nombre_completo"
                  value={formData.nombre_completo}
                  onChange={(e) => updateField("nombre_completo", e.target.value)}
                  className={inputStyles}
                  placeholder="Tu nombre"
                  required
                />
              </div>
              <div>
                <Label className={labelStyles} htmlFor="correo_empresa">Correo de empresa *</Label>
                <Input
                  data-testid="input-correo"
                  id="correo_empresa"
                  type="email"
                  value={formData.correo_empresa}
                  onChange={(e) => updateField("correo_empresa", e.target.value)}
                  className={inputStyles}
                  placeholder="tu@empresa.com"
                  required
                />
              </div>
              <div>
                <Label className={labelStyles} htmlFor="telefono">Teléfono de contacto</Label>
                <Input
                  data-testid="input-telefono"
                  id="telefono"
                  value={formData.telefono}
                  onChange={(e) => updateField("telefono", e.target.value)}
                  className={inputStyles}
                  placeholder="+52 123 456 7890"
                />
              </div>
              <div>
                <Label className={labelStyles} htmlFor="nombre_empresa">Nombre de la empresa *</Label>
                <Input
                  data-testid="input-empresa"
                  id="nombre_empresa"
                  value={formData.nombre_empresa}
                  onChange={(e) => updateField("nombre_empresa", e.target.value)}
                  className={inputStyles}
                  placeholder="Nombre de tu empresa"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <Label className={labelStyles} htmlFor="sitio_web">Sitio web o redes sociales</Label>
                <Input
                  data-testid="input-sitio-web"
                  id="sitio_web"
                  value={formData.sitio_web}
                  onChange={(e) => updateField("sitio_web", e.target.value)}
                  className={inputStyles}
                  placeholder="https://tuempresa.com"
                />
              </div>
            </div>
          </motion.div>

          {/* Block 2 - Company */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="form-block border border-white/[0.06] p-8 sm:p-10 bg-[#0A0A0A]"
          >
            <h3
              className="text-lg font-semibold text-white mb-8 flex items-center gap-3"
              style={{ fontFamily: "'Syne', sans-serif" }}
            >
              <span className="text-xs font-mono text-neutral-500 border border-white/10 w-7 h-7 flex items-center justify-center">2</span>
              Sobre tu empresa
            </h3>
            <div>
              <Label className={labelStyles}>Sector o giro principal *</Label>
              <Select
                value={formData.sector}
                onValueChange={(val) => updateField("sector", val)}
              >
                <SelectTrigger
                  data-testid="select-sector"
                  className="bg-transparent border-0 border-b border-white/20 rounded-none text-white focus:ring-0 h-12 text-sm"
                >
                  <SelectValue placeholder="Selecciona tu sector" />
                </SelectTrigger>
                <SelectContent className="bg-[#141414] border-white/10 text-white">
                  {sectors.map((s) => (
                    <SelectItem key={s} value={s} className="text-white focus:bg-white/10 focus:text-white">
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.sector === "Otro" && (
                <Input
                  data-testid="input-sector-otro"
                  value={formData.sector_otro}
                  onChange={(e) => updateField("sector_otro", e.target.value)}
                  className={`${inputStyles} mt-4`}
                  placeholder="Especifica tu sector"
                />
              )}
            </div>
          </motion.div>

          {/* Block 3 - Project */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="form-block border border-white/[0.06] p-8 sm:p-10 bg-[#0A0A0A]"
          >
            <h3
              className="text-lg font-semibold text-white mb-8 flex items-center gap-3"
              style={{ fontFamily: "'Syne', sans-serif" }}
            >
              <span className="text-xs font-mono text-neutral-500 border border-white/10 w-7 h-7 flex items-center justify-center">3</span>
              Sobre tu proyecto
            </h3>

            <div className="space-y-10">
              {/* 7. Tipo de proyecto */}
              <div>
                <Label className={labelStyles}>Tipo de proyecto que buscas *</Label>
                <p className="text-xs text-neutral-500 mb-4">Selección múltiple</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {projectTypes.map((pt) => (
                    <label
                      key={pt}
                      className="flex items-start gap-3 p-3 border border-white/[0.06] hover:border-white/15 transition-colors cursor-pointer group"
                    >
                      <Checkbox
                        data-testid={`checkbox-project-${pt.slice(0, 20).replace(/\s/g, '-').toLowerCase()}`}
                        checked={formData.tipo_proyecto.includes(pt)}
                        onCheckedChange={() => toggleCheckbox("tipo_proyecto", pt)}
                        className="mt-0.5 border-white/30 data-[state=checked]:bg-white data-[state=checked]:text-black"
                      />
                      <span className="text-sm text-neutral-300 group-hover:text-white transition-colors leading-snug">
                        {pt}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* 8. Estado del proyecto */}
              <div>
                <Label className={labelStyles}>Estado de tu proyecto *</Label>
                <RadioGroup
                  data-testid="radio-estado"
                  value={formData.estado_proyecto}
                  onValueChange={(val) => updateField("estado_proyecto", val)}
                  className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3"
                >
                  {projectStates.map((ps) => (
                    <label
                      key={ps}
                      className="flex items-start gap-3 p-3 border border-white/[0.06] hover:border-white/15 transition-colors cursor-pointer group"
                    >
                      <RadioGroupItem
                        value={ps}
                        className="mt-0.5 border-white/30 text-white"
                      />
                      <span className="text-sm text-neutral-300 group-hover:text-white transition-colors leading-snug">
                        {ps}
                      </span>
                    </label>
                  ))}
                </RadioGroup>
              </div>

              {/* 9. Descripcion del reto */}
              <div>
                <Label className={labelStyles} htmlFor="descripcion_reto">Describe brevemente tu reto</Label>
                <Textarea
                  data-testid="textarea-reto"
                  id="descripcion_reto"
                  value={formData.descripcion_reto}
                  onChange={(e) => updateField("descripcion_reto", e.target.value)}
                  className="bg-transparent border border-white/[0.06] rounded-none text-white focus-visible:ring-0 focus-visible:border-white/30 transition-colors placeholder:text-neutral-600 text-sm min-h-[100px] resize-none"
                  placeholder="Cuéntanos en 2-4 líneas el reto que quieres resolver..."
                  maxLength={500}
                />
                <span className="text-[10px] text-neutral-600 mt-1 block text-right">
                  {formData.descripcion_reto.length}/500
                </span>
              </div>

              {/* 10. Timeline */}
              <div>
                <Label className={labelStyles}>¿Cuándo te gustaría iniciar?</Label>
                <RadioGroup
                  data-testid="radio-timeline"
                  value={formData.timeline}
                  onValueChange={(val) => updateField("timeline", val)}
                  className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3"
                >
                  {timelines.map((t) => (
                    <label
                      key={t}
                      className="flex items-start gap-3 p-3 border border-white/[0.06] hover:border-white/15 transition-colors cursor-pointer group"
                    >
                      <RadioGroupItem
                        value={t}
                        className="mt-0.5 border-white/30 text-white"
                      />
                      <span className="text-sm text-neutral-300 group-hover:text-white transition-colors leading-snug">
                        {t}
                      </span>
                    </label>
                  ))}
                </RadioGroup>
              </div>

              {/* 11. Objetivo principal */}
              <div>
                <Label className={labelStyles}>Objetivo principal del proyecto</Label>
                <p className="text-xs text-neutral-500 mb-4">Selección múltiple</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {objectives.map((obj) => (
                    <label
                      key={obj}
                      className="flex items-start gap-3 p-3 border border-white/[0.06] hover:border-white/15 transition-colors cursor-pointer group"
                    >
                      <Checkbox
                        data-testid={`checkbox-obj-${obj.slice(0, 15).replace(/\s/g, '-').toLowerCase()}`}
                        checked={formData.objetivo_principal.includes(obj)}
                        onCheckedChange={() => toggleCheckbox("objetivo_principal", obj)}
                        className="mt-0.5 border-white/30 data-[state=checked]:bg-white data-[state=checked]:text-black"
                      />
                      <span className="text-sm text-neutral-300 group-hover:text-white transition-colors leading-snug">
                        {obj}
                      </span>
                    </label>
                  ))}
                </div>
                <Input
                  data-testid="input-objetivo-otro"
                  value={formData.objetivo_otro}
                  onChange={(e) => updateField("objetivo_otro", e.target.value)}
                  className={`${inputStyles} mt-4`}
                  placeholder="Otro objetivo (opcional)"
                />
              </div>

              {/* 12. Interes en diagnostico */}
              <div>
                <Label className={labelStyles}>¿Te interesa agendar una sesión de diagnóstico?</Label>
                <RadioGroup
                  data-testid="radio-diagnostico"
                  value={formData.interes_diagnostico}
                  onValueChange={(val) => updateField("interes_diagnostico", val)}
                  className="grid grid-cols-1 gap-3 mt-3"
                >
                  {diagOptions.map((opt) => (
                    <label
                      key={opt.value}
                      className="flex items-start gap-3 p-4 border border-white/[0.06] hover:border-white/15 transition-colors cursor-pointer group"
                    >
                      <RadioGroupItem
                        value={opt.value}
                        className="mt-0.5 border-white/30 text-white"
                      />
                      <span className="text-sm text-neutral-300 group-hover:text-white transition-colors">
                        {opt.label}
                      </span>
                    </label>
                  ))}
                </RadioGroup>
              </div>
            </div>
          </motion.div>

          {/* hCaptcha + Submit */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="flex flex-col items-center"
          >
            <div ref={captchaRef} className="mb-6"></div>
            <button
              type="submit"
              disabled={loading}
              data-testid="submit-btn"
              className="group bg-white text-black font-bold text-sm tracking-[0.15em] uppercase px-12 py-5 hover:bg-neutral-200 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Enviar mi solicitud
                </>
              )}
            </button>
            <p className="text-xs text-neutral-600 mt-4 text-center max-w-sm">
              Usaremos tus datos únicamente para gestionar tu solicitud.
            </p>
          </motion.div>
        </form>
      </div>
    </section>
  );
});

LeadForm.displayName = "LeadForm";
export default LeadForm;
